import { useState, useRef, useCallback, useEffect } from "react";

/**
 * Real-time speech-to-text using native Web Speech API.
 * Auto-detects silence and triggers onSilence callback.
 * Returns: { transcript, recording, startRecording, stopRecording, liveText }
 */
const WhisperVoiceConfig = ({ onSilence, silenceTimeout = 2500 } = {}) => {
  const avatarlanguage = sessionStorage.getItem("language");
  let languageCode = "en-US";

  if (["English", "EN"].includes(avatarlanguage)) {
    languageCode = "en-US";
  } else if (["Indonesia", "ID"].includes(avatarlanguage)) {
    languageCode = "id-ID";
  }

  const [recording, setRecording] = useState(false);
  const [liveText, setLiveText] = useState("");
  const recordingRef = useRef(false);
  const transcriptRef = useRef({ text: null });
  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const onSilenceRef = useRef(onSilence);
  const silenceTimeoutRef = useRef(silenceTimeout);
  const hasSpokenRef = useRef(false);

  useEffect(() => {
    onSilenceRef.current = onSilence;
  }, [onSilence]);

  useEffect(() => {
    silenceTimeoutRef.current = silenceTimeout;
  }, [silenceTimeout]);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const startSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      if (
        hasSpokenRef.current &&
        recordingRef.current &&
        transcriptRef.current.text
      ) {
        console.log("[Speech] Silence detected, auto-sending...");
        if (onSilenceRef.current) {
          onSilenceRef.current(transcriptRef.current.text);
        }
      }
    }, silenceTimeoutRef.current);
  }, [clearSilenceTimer]);

  const getRecognition = useCallback(() => {
    // Clean up previous instance
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      } catch (e) { /* */ }
      recognitionRef.current = null;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.error("[Speech] SpeechRecognition not supported in this browser");
      return null;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = languageCode;

    recognition.onresult = (event) => {
      let finalText = "";
      let interimText = "";

      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += result[0].transcript + " ";
        } else {
          interimText += result[0].transcript;
        }
      }

      const formatText = (str) => {
        if (!str) return "";
        let formatted = str.charAt(0).toUpperCase() + str.slice(1);
        const questionWords = [
          "apa", "siapa", "kapan", "dimana", "di mana", "kenapa", "mengapa", "bagaimana", "berapa", "apakah",
          "what", "who", "when", "where", "why", "how", "is", "are", "do", "does", "did", "can", "could", "would", "should", "may", "am"
        ];
        const firstWord = formatted.toLowerCase().split(" ")[0];
        const isQuestion = questionWords.includes(firstWord) || firstWord.endsWith("kah");
        
        if (isQuestion && !formatted.endsWith("?")) {
          formatted += "?";
        }
        return formatted;
      };

      const text = formatText((finalText + interimText).trim());
      transcriptRef.current.text = text || null;
      setLiveText(text);

      if (text) {
        hasSpokenRef.current = true;
        startSilenceTimer();
      }
    };

    recognition.onerror = (event) => {
      console.error("[Speech] Recognition error:", event.error);
      if (event.error === "not-allowed" || event.error === "audio-capture") {
        recordingRef.current = false;
        setRecording(false);
        clearSilenceTimer();
      }
      // "aborted" is expected when we call stop(), ignore it
    };

    recognition.onend = () => {
      if (recordingRef.current) {
        try {
          setTimeout(() => {
            if (recordingRef.current && recognitionRef.current) {
              recognitionRef.current.start();
            }
          }, 100);
        } catch (e) {
          // ignore
        }
      }
    };

    recognitionRef.current = recognition;
    return recognition;
  }, [languageCode, startSilenceTimer, clearSilenceTimer]);

  const startRecording = useCallback(() => {
    transcriptRef.current.text = null;
    setLiveText("");
    hasSpokenRef.current = false;
    clearSilenceTimer();

    const recognition = getRecognition();
    if (recognition) {
      try {
        recognition.start();
        recordingRef.current = true;
        setRecording(true);
      } catch (e) {
        console.error("[Speech] Failed to start:", e);
      }
    }
  }, [getRecognition, clearSilenceTimer]);

  const stopRecording = useCallback(() => {
    recordingRef.current = false;
    clearSilenceTimer();
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onend = null; // prevent auto-restart
        recognitionRef.current.abort(); // immediately release mic
      } catch (e) {
        // already stopped
      }
      recognitionRef.current = null;
    }
    setRecording(false);
  }, [clearSilenceTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearSilenceTimer();
      if (recognitionRef.current) {
        try {
          recognitionRef.current.onend = null;
          recognitionRef.current.abort();
        } catch (e) { /* */ }
        recognitionRef.current = null;
      }
    };
  }, [clearSilenceTimer]);

  return {
    transcript: transcriptRef.current,
    recording,
    startRecording,
    stopRecording,
    liveText,
  };
};

export default WhisperVoiceConfig;
